import React, { useMemo } from 'react';
import {
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
  Cell
} from 'recharts';

const DoisChart = ({ data, spotPrice }) => {
  // Transform data for horizontal bar chart
  const chartData = useMemo(() => {
    if (!data || !spotPrice) return [];

    const range = spotPrice * 0.05; // 5% range
    return data
      .filter(item => item.strike >= spotPrice - range && item.strike <= spotPrice + range)
      .map(item => ({
        strike: item.strike,
        dois: item.dois, // Already -1 to +1 range
        callOi: item.callOi,
        putOi: item.putOi,
        isPositive: item.dois >= 0
      }));
  }, [data, spotPrice]);

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;

    const data = payload[0].payload;
    const sentiment = data.dois > 0.3 ? 'Bullish' : data.dois < -0.3 ? 'Bearish' : 'Neutral';
    return (
      <div style={styles.tooltip}>
        <div style={styles.tooltipTitle}>Strike: {data.strike}</div>
        <div style={{ color: data.dois >= 0 ? '#9966ff' : '#ff66cc' }}>
          DOIS: {data.dois >= 0 ? '+' : ''}{(data.dois * 100).toFixed(1)}% ({sentiment})
        </div>
        <div style={{ color: '#9966ff' }}>
          Call OI: {data.callOi.toLocaleString()}
        </div>
        <div style={{ color: '#ff66cc' }}>
          Put OI: {data.putOi.toLocaleString()}
        </div>
        <div style={{ color: '#8b949e' }}>
          P/C Ratio: {data.callOi > 0 ? (data.putOi / data.callOi).toFixed(2) : 'N/A'}
        </div>
      </div>
    );
  };

  if (!chartData.length) {
    return (
      <div style={styles.noData}>
        No DOIS data available
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <h2 style={styles.chartTitle}>Dealer Open Interest Sentiment (DOIS)</h2>
      <div style={styles.legend}>
        <span style={{ ...styles.legendItem, color: '#9966ff' }}>Call Heavy (Bullish)</span>
        <span style={{ ...styles.legendItem, color: '#ff66cc' }}>Put Heavy (Bearish)</span>
      </div>
      <ResponsiveContainer width="100%" height={400}>
        <ComposedChart
          layout="vertical"
          data={chartData}
          margin={{ top: 20, right: 30, left: 60, bottom: 20 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#30363d" horizontal={true} vertical={true} />
          <XAxis
            type="number"
            domain={[-1, 1]}
            tick={{ fill: '#8b949e', fontSize: 12 }}
            tickFormatter={(value) => `${(value * 100).toFixed(0)}%`}
            axisLine={{ stroke: '#30363d' }}
            ticks={[-1, -0.5, 0, 0.5, 1]}
          />
          <YAxis
            type="category"
            dataKey="strike"
            tick={{ fill: '#8b949e', fontSize: 11 }}
            width={55}
            axisLine={{ stroke: '#30363d' }}
            tickFormatter={(value) => value.toLocaleString()}
            reversed={true}
          />
          <Tooltip content={<CustomTooltip />} />
          <ReferenceLine x={0} stroke="#58a6ff" strokeWidth={1} />
          {spotPrice && (
            <ReferenceLine
              y={chartData.find(d => Math.abs(d.strike - spotPrice) ===
                Math.min(...chartData.map(c => Math.abs(c.strike - spotPrice))))?.strike}
              stroke="#ffcc00"
              strokeWidth={2}
              strokeDasharray="5 5"
              label={{
                value: `Spot: ${spotPrice.toFixed(0)}`,
                fill: '#ffcc00',
                fontSize: 12,
                position: 'right'
              }}
            />
          )}
          <Bar dataKey="dois" name="DOIS" radius={[0, 4, 4, 0]}>
            {chartData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={entry.isPositive ? '#9966ff' : '#ff66cc'}
                fillOpacity={0.8}
              />
            ))}
          </Bar>
        </ComposedChart>
      </ResponsiveContainer>
      <div style={styles.footer}>
        <p style={styles.footerText}>
          DOIS = (Call OI - Put OI) / Total OI. Range: -100% (all puts) to +100% (all calls).
        </p>
      </div>
    </div>
  );
};

const styles = {
  container: {
    backgroundColor: '#161b22',
    borderRadius: '12px',
    border: '1px solid #30363d',
    padding: '24px'
  },
  chartTitle: {
    margin: '0 0 8px 0',
    fontSize: '18px',
    fontWeight: 600,
    color: '#ffffff'
  },
  legend: {
    display: 'flex',
    gap: '24px',
    marginBottom: '16px',
    fontSize: '12px'
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  tooltip: {
    backgroundColor: '#1c2128',
    border: '1px solid #30363d',
    borderRadius: '8px',
    padding: '12px 16px',
    fontSize: '13px',
    lineHeight: 1.6
  },
  tooltipTitle: {
    fontWeight: 600,
    color: '#ffffff',
    marginBottom: '4px'
  },
  footer: {
    marginTop: '16px',
    paddingTop: '16px',
    borderTop: '1px solid #30363d'
  },
  footerText: {
    margin: 0,
    fontSize: '12px',
    color: '#8b949e',
    lineHeight: 1.5
  },
  noData: {
    textAlign: 'center',
    padding: '48px',
    color: '#8b949e',
    backgroundColor: '#161b22',
    borderRadius: '12px',
    border: '1px solid #30363d'
  }
};

export default DoisChart;
